// Canvas waveform: scrub to seek, optional trim handles, song markers. Peaks are 0..1 from the server.
import { useCallback, useEffect, useRef, useState } from "react";
import { fmt } from "./vbShared";

const HANDLE_PX = 14;

export const Waveform = ({ peaks, duration, time = 0, onSeek, trim, onTrim, markers = [], cuts = [], regions = [], selecting = false, onSelect, onTap, height = 96, accent = "#F2CC8F", testid = "waveform" }) => {
  const wrap = useRef(null);
  const canvas = useRef(null);
  const [w, setW] = useState(0);
  const [hover, setHover] = useState(null);
  const [sel, setSel] = useState(null);
  const drag = useRef(null);

  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setW(Math.floor(e.contentRect.width)));
    if (wrap.current) ro.observe(wrap.current);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const c = canvas.current;
    if (!c || !w) return;
    const dpr = window.devicePixelRatio || 1;
    c.width = w * dpr; c.height = height * dpr;
    const g = c.getContext("2d");
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, w, height);
    const bars = Math.max(1, Math.floor(w / 3));
    const src = peaks?.length ? peaks : null;
    const playedX = duration ? (time / duration) * w : 0;
    const ts = trim && duration ? (trim.start / duration) * w : 0;
    const te = trim && duration ? ((trim.end ?? duration) / duration) * w : w;
    for (let i = 0; i < bars; i += 1) {
      let p = 0.04;
      if (src) {
        const a = Math.floor((i / bars) * src.length), b = Math.max(a + 1, Math.floor(((i + 1) / bars) * src.length));
        p = Math.max(0.04, ...src.slice(a, b));
      }
      const x = i * 3, bh = Math.max(2, p * height * 0.9);
      const xs = duration ? (x / w) * duration : 0;
      const outside = (trim && (x < ts || x > te)) || cuts.some(([a, b]) => xs >= a && xs < b);
      g.fillStyle = outside ? "rgba(255,255,255,0.09)" : x < playedX ? accent : "rgba(255,255,255,0.32)";
      g.fillRect(x, (height - bh) / 2, 2, bh);
    }
    if (trim) {
      g.fillStyle = "rgba(8,14,20,0.45)";
      g.fillRect(0, 0, ts, height); g.fillRect(te, 0, w - te, height);
      [ts, te].forEach((x) => { g.fillStyle = accent; g.fillRect(Math.min(w - 2, Math.max(0, x - 1)), 0, 2, height); });
    }
    g.fillStyle = "rgba(84,227,255,0.85)";
    markers.forEach((m) => { if (duration) g.fillRect((m.t / duration) * w, 0, 1.5, 8); });
    if (duration) { g.fillStyle = "#fff"; g.fillRect(Math.min(w - 1.5, playedX), 0, 1.5, height); }
    if (duration) cuts.forEach(([a, b]) => { g.fillStyle = "rgba(8,14,20,0.5)"; g.fillRect((a / duration) * w, 0, ((b - a) / duration) * w, height); });
    if (duration) [...regions, ...(sel ? [{ start: Math.min(sel[0], sel[1]), end: Math.max(sel[0], sel[1]), color: "#F2CC8F", active: true }] : [])].forEach((r) => {
      const x = (r.start / duration) * w, rw = Math.max(2, ((r.end - r.start) / duration) * w);
      g.globalAlpha = r.active ? 0.28 : 0.14; g.fillStyle = r.color; g.fillRect(x, 0, rw, height);
      g.globalAlpha = 1; g.fillRect(x, height - 4, rw, 4);
    });
  }, [peaks, duration, time, trim, markers, cuts, regions, sel, w, height, accent]);

  const secAt = useCallback((clientX) => {
    const r = wrap.current.getBoundingClientRect();
    return Math.min(1, Math.max(0, (clientX - r.left) / r.width)) * (duration || 0);
  }, [duration]);

  const onDown = (e) => {
    if (!duration) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    const r = wrap.current.getBoundingClientRect();
    const x = e.clientX - r.left;
    if (trim && onTrim) {
      const ts = (trim.start / duration) * r.width, te = ((trim.end ?? duration) / duration) * r.width;
      if (Math.abs(x - ts) < HANDLE_PX) { drag.current = "start"; return; }
      if (Math.abs(x - te) < HANDLE_PX) { drag.current = "end"; return; }
    }
    if (selecting) { drag.current = "select"; const s0 = secAt(e.clientX); setSel([s0, s0]); return; }
    drag.current = "seek";
    onSeek?.(secAt(e.clientX));
  };
  const onMove = (e) => {
    if (!duration) return;
    const s = secAt(e.clientX);
    setHover({ s, x: e.clientX - wrap.current.getBoundingClientRect().left });
    if (drag.current === "select") setSel((v) => (v ? [v[0], s] : v));
    else if (drag.current === "seek") onSeek?.(s);
    else if (drag.current === "start") onTrim?.(Math.min(s, (trim.end ?? duration) - 1), trim.end);
    else if (drag.current === "end") onTrim?.(trim.start, Math.max(s, trim.start + 1));
  };
  const onUp = () => {
    if (drag.current === "select" && sel) { const a = Math.min(sel[0], sel[1]), b = Math.max(sel[0], sel[1]); if (b - a >= 1) onSelect?.(a, b); else onTap?.(a); setSel(null); }
    drag.current = null;
  };

  return (
    <div ref={wrap} className={`relative w-full select-none touch-none ${selecting ? "cursor-crosshair" : "cursor-pointer"}`} style={{ height }} data-selecting={selecting ? "true" : "false"} onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp} onPointerLeave={() => setHover(null)} data-testid={testid}>
      <canvas ref={canvas} className="absolute inset-0 h-full w-full" />
      {!peaks?.length && <span className="absolute inset-0 flex items-center justify-center text-[11px] text-white/40">Drawing the waveform…</span>}
      {hover && (
        <span className="pointer-events-none absolute -top-6 -translate-x-1/2 rounded-md bg-black/80 px-1.5 py-0.5 text-[10px] font-bold tabular-nums text-white" style={{ left: hover.x }}>{fmt(hover.s)}</span>
      )}
    </div>
  );
};
